import {
    RightOutlined,
    HomeOutlined,
    UserOutlined,
    FileOutlined,
    FileAddOutlined,
    SettingOutlined,
    EllipsisOutlined,
    MinusOutlined,
    HomeFilled,
    FileFilled,
    FileAddFilled,
    SettingFilled,
    TeamOutlined,
    FileTextFilled,
    FileTextOutlined,
    TableOutlined,
} from '@ant-design/icons';
import IrelandFlagIcon from 'components/icons/IrelandFlag';
import UkFlagIcon from 'components/icons/UkFlag';
import EuFlagIcon from 'components/icons/EuFlag';
import { NodeModel } from '@minoru/react-dnd-treeview';
import { Dropdown, Menu } from 'antd';
import SuitCaseIcon from 'components/icons/SuitCaseIcon';
import UserFilled from 'components/icons/UserFilled';
import useDeclarations from 'hooks/useDeclarations';
import useSession from 'hooks/useSession';
import { FC, ReactNode, RefObject, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { IndividualType } from 'store/individuals/individual';
import styles from './SidebarNode.module.css';
import sidebarStyles from './Sidebar.module.css';
import ShipmentIcon from 'components/icons/ShipmentIcon';
import ShipmentIconOutlined from 'components/icons/ShipmentIconOutlined';
import SuitCaseIconFilled from 'components/icons/SuitCaseFilled';

export const Icons = {
    home: HomeOutlined,
    customers: UserOutlined,
    sharedFiles: FileOutlined,
    jobs: SuitCaseIcon,
    templates: FileAddOutlined,
    settings: SettingOutlined,
    irelandFlag: IrelandFlagIcon,
    ukFlag: UkFlagIcon,
    euFlag: EuFlagIcon,
    shipments: ShipmentIconOutlined,
    teams: TeamOutlined,
    invoices: FileTextOutlined,
    table: TableOutlined,
};

export const FilledIcons = {
    home: HomeFilled,
    sharedFiles: FileFilled,
    templates: FileAddFilled,
    settings: SettingFilled,
    customers: UserFilled,
    jobs: SuitCaseIconFilled,
    irelandFlag: IrelandFlagIcon,
    ukFlag: UkFlagIcon,
    euFlag: EuFlagIcon,
    shipments: ShipmentIcon,
    teams: TeamOutlined,
    invoices: FileTextFilled,
    table: TableOutlined,
};

export type SidebarNodeType = NodeModel<{
    icon?: keyof typeof Icons;
    url?: string;
    activeOn?: string | RegExp;
    customTrigger?: any;
    favourite?: boolean;
    forIndividualType?: { value: IndividualType[]; not?: boolean };
}>;

type Props = {
    node: SidebarNodeType;
    depth: number;
    isOpen: boolean;
    onToggle: (id: NodeModel['id']) => void;
    handleRef: RefObject<any>;
    favourites?: SidebarNodeType[];
    actions?: { text: string; action: string; onAction: (node: SidebarNodeType) => void; hidden?: boolean }[];
    collapsed?: any;
};

export const SidebarNode: React.FC<Props> = (props) => {
    const { pathname } = useLocation();
    const { declaration, isLoading } = useDeclarations();
    const { userInfo } = useSession();

    const handleToggle = (e: React.MouseEvent) => {
        e.stopPropagation();
        props.onToggle(props.node.id);
    };

    const active = useMemo(() => {
        if (props.node.droppable) return false;

        if (pathname.includes('/declarations')) {
            if (isLoading) return false;
            if (!declaration) return false;
            const country = declaration.declarationExternalEntity === 'REVENUE' ? 'ireland' : 'uk';
            const internalType = declaration.declarationInternalType;

            const declarationType = `${country}/${internalType}`;
            return props.node.data?.customTrigger === declarationType;
        }

        if (!props.node.data?.activeOn) {
            if (!props.node.data?.url) return false;
            return pathname.includes(props.node.data.url);
        }

        if (props.node.data.activeOn instanceof RegExp) return props.node.data.activeOn.test(pathname);
        return pathname.includes(props.node.data.activeOn);
    }, [
        declaration,
        pathname,
        isLoading,
        props.node.data?.activeOn,
        props.node.data?.url,
        props.node.droppable,
        props.node.data?.customTrigger,
    ]);

    const Icon = useMemo(
        () =>
            props.node.data?.icon
                ? active
                    ? (FilledIcons[props.node.data.icon] as FC)
                    : (Icons[props.node.data.icon] as FC)
                : () => <></>,
        [active, props.node]
    );

    const collapsed = !!props.collapsed;

    if (collapsed && props.depth > 0) return null;

    const actions = props.actions?.filter((action) => !action.hidden);

    if (props.node.data?.forIndividualType && userInfo?.role !== undefined) {
        const includes = props.node.data.forIndividualType.value.includes(userInfo.role);
        let show;
        if (props.node.data.forIndividualType.not) show = !includes;
        else show = includes;
        if (!show) return <span id="hidden" />;
    }

    return (
        <div ref={props.handleRef} className={`tree-node ${styles.root} ${props.depth > 0 && styles.notZeroDepth}`}>
            <Wrapper
                to={{ pathname: props.node.data?.url ?? '' }}
                droppable={props.node.droppable}
                style={{
                    marginTop: props.node.data?.url?.includes('settings') ? '20px' : '0',
                    alignItems: props.node.data?.url?.includes('settings') ? 'end' : '0',
                }}
                className={`${styles.content} ${collapsed && styles.collapsed} ${active ? styles.active : ''}`}
                onToggle={handleToggle}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    {props.node.text?.includes('Customs') && collapsed ? (
                        <Dropdown
                            overlay={
                                <Menu style={{ borderRadius: '8px' }} className={sidebarStyles.menu}>
                                    {!!collapsed &&
                                        props.collapsed.length &&
                                        props.collapsed
                                            ?.filter((item: any) => item.parent === props.node.id)
                                            ?.map((item: any) => (
                                                <Menu.Item title="" className={sidebarStyles.menuItem} key={item.id}>
                                                    <Link to={item.data?.url} style={{ background: 'white' }} replace>
                                                        <span className={sidebarStyles.menuSpan}>{item.text}</span>
                                                    </Link>
                                                </Menu.Item>
                                            ))}
                                </Menu>
                            }
                            trigger={['click']}
                        >
                            <Icon />
                        </Dropdown>
                    ) : (
                        <Icon />
                    )}
                    {!collapsed && props.node.text}
                </div>
                {!collapsed && props.node.droppable && (
                    <div className={`${styles.expandIconWrapper} ${props.isOpen ? styles.isOpen : ''}`}>
                        <RightOutlined />
                    </div>
                )}
                {!collapsed &&
                    !!actions?.length &&
                    (props.favourites?.some((fav) => fav.id === props.node.id) ? (
                        <MinusOutlined
                            className={`${styles.favourite}`}
                            onClick={() => {
                                actions[0]?.onAction(props.node);
                            }}
                        />
                    ) : (
                        <Dropdown
                            trigger={['click']}
                            overlay={
                                <Menu>
                                    {actions.map((action) => (
                                        <Menu.Item key={action.action} onClick={() => action.onAction(props.node)}>
                                            <span style={{ fontSize: '12px' }}>{action.text}</span>
                                        </Menu.Item>
                                    ))}
                                </Menu>
                            }
                            placement="bottomRight"
                        >
                            <EllipsisOutlined className={`${styles.favourite}`} />
                        </Dropdown>
                    ))}
            </Wrapper>
        </div>
    );
};

const Wrapper = (
    props: {
        children: ReactNode | ReactNode[];
        className?: string;
        style?: React.CSSProperties;
    } & (
        | {
              droppable: true;
              onToggle: (e: any) => void;
          }
        | {
              droppable?: false;
              to: { pathname: string };
          }
    )
) => {
    return props.droppable ? (
        <div
            className={props.className}
            onClick={props.onToggle}
            style={{ cursor: 'pointer', ...(props.style ? props.style : {}) }}
        >
            {props.children}
        </div>
    ) : (
        <Link to={props.to} className={props.className} style={props.style}>
            {props.children}
        </Link>
    );
};
